/**
 * ArchlangServices — facade over the workspace + indexes + providers.
 *
 * Owns the rebuild lifecycle: whenever a file changes, indexes are
 * rebuilt from the workspace, and providers are handed fresh references.
 * LSP server wiring lives one layer up (see server.ts); this class is
 * fully unit-testable on its own.
 */

import type {
  CompletionItem,
  CompletionList,
  Diagnostic,
  DocumentHighlight,
  DocumentSymbol,
  FoldingRange,
  Hover,
  Location,
  SemanticTokens,
  SymbolInformation,
  WorkspaceEdit,
} from "vscode-languageserver";
import { Workspace } from "./workspace/workspace.js";
import type { StdlibExport } from "./workspace/stdlib-export-index.js";
import { SymbolTable } from "./workspace/symbol-table.js";
import { ReferenceIndex } from "./workspace/reference-index.js";
import { CursorContext } from "./workspace/cursor-context.js";
import { toLspDiagnostics } from "./providers/diagnostics.js";
import { provideDefinition } from "./providers/definition.js";
import { provideTypeDefinition } from "./providers/type-definition.js";
import { provideReferences } from "./providers/references.js";
import { provideDocumentHighlight } from "./providers/document-highlight.js";
import { provideHover } from "./providers/hover.js";
import { provideDocumentSymbols } from "./providers/document-symbols.js";
import { provideWorkspaceSymbols } from "./providers/workspace-symbols.js";
import {
  provideCompletion,
  resolveCompletion,
  toCompletionList,
  FULL_COMPLETION_CAPS,
  type ClientCompletionCaps,
} from "./providers/completion.js";
import { provideSemanticTokens } from "./providers/semantic-tokens.js";
import { provideFoldingRanges } from "./providers/folding.js";
import { prepareRename, provideRename } from "./providers/rename.js";
import { formatDocument, type FormatterOptions } from "./providers/formatter.js";
import { provideCodeActions } from "./providers/code-actions.js";
import { provideCodeLens } from "./providers/code-lens.js";
import { provideDocumentLinks } from "./providers/document-links.js";
import { provideSelectionRanges } from "./providers/selection-range.js";
import { provideSignatureHelp } from "./providers/signature-help.js";
import {
  prepareCallHierarchy,
  incomingCalls,
  outgoingCalls,
} from "./providers/call-hierarchy.js";
import type {
  CallHierarchyIncomingCall,
  CallHierarchyItem,
  CallHierarchyOutgoingCall,
  CodeAction,
  CodeLens,
  DocumentLink,
  Position,
  Range,
  SelectionRange,
  SignatureHelp,
  TextEdit,
} from "vscode-languageserver";

export class ArchlangServices {
  readonly workspace = new Workspace();
  private symbols: SymbolTable;
  private refs: ReferenceIndex;
  private cursor: CursorContext;
  /** Stdlib export catalogue for auto-import (P3). Injected once by the
   *  package router after bootstrap (static per session); empty until then, so
   *  completion simply offers no auto-import items. */
  private stdlibExports: readonly StdlibExport[] = [];

  constructor() {
    this.symbols = new SymbolTable(this.workspace);
    this.refs = new ReferenceIndex(this.workspace, this.symbols);
    this.cursor = new CursorContext(this.workspace);
  }

  /** Supply the stdlib export index (auto-import source, P3). */
  setStdlibExports(exports: readonly StdlibExport[]): void {
    this.stdlibExports = exports;
  }

  setFile(uri: string, text: string, version = 0): void {
    this.workspace.setFile(uri, text, version);
    this.rebuildIndexes();
  }

  /** Batch seed — one workspace rebuild + one index rebuild for the whole set.
   *  The bootstrap path; see `Workspace.setFiles`. */
  setFiles(entries: readonly { uri: string; text: string; version?: number }[]): void {
    if (entries.length === 0) return;
    this.workspace.setFiles(entries);
    this.rebuildIndexes();
  }

  removeFile(uri: string): void {
    this.workspace.removeFile(uri);
    this.rebuildIndexes();
  }

  diagnostics(uri: string, locale?: string): Diagnostic[] {
    return toLspDiagnostics(this.workspace.diagnosticsFor(uri), locale);
  }

  hover(uri: string, line: number, character: number): Hover | null {
    return provideHover(
      this.workspace,
      this.symbols,
      this.cursor,
      this.refs,
      uri,
      line,
      character
    );
  }

  definition(uri: string, line: number, character: number): Location | null {
    return provideDefinition(
      this.workspace,
      this.symbols,
      this.cursor,
      uri,
      line,
      character
    );
  }

  typeDefinition(uri: string, line: number, character: number): Location | null {
    return provideTypeDefinition(
      this.workspace,
      this.symbols,
      this.cursor,
      uri,
      line,
      character
    );
  }

  references(
    uri: string,
    line: number,
    character: number,
    includeDeclaration: boolean
  ): Location[] {
    return provideReferences(
      this.workspace,
      this.symbols,
      this.cursor,
      this.refs,
      uri,
      line,
      character,
      includeDeclaration
    );
  }

  documentHighlight(uri: string, line: number, character: number): DocumentHighlight[] {
    return provideDocumentHighlight(
      this.workspace,
      this.symbols,
      this.cursor,
      this.refs,
      uri,
      line,
      character
    );
  }

  documentSymbols(uri: string): DocumentSymbol[] {
    return provideDocumentSymbols(this.workspace, uri);
  }

  workspaceSymbols(query: string): SymbolInformation[] {
    return provideWorkspaceSymbols(this.symbols, query);
  }

  completion(
    uri: string,
    line: number,
    character: number,
    caps: ClientCompletionCaps = FULL_COMPLETION_CAPS
  ): CompletionList {
    const items = provideCompletion(
      this.workspace,
      this.symbols,
      uri,
      line,
      character,
      this.refs,
      this.stdlibExports
    );
    return toCompletionList(items, caps);
  }

  resolveCompletion(item: CompletionItem): CompletionItem {
    return resolveCompletion(this.workspace, item);
  }

  semanticTokens(uri: string): SemanticTokens {
    const file = this.workspace.getFile(uri);
    if (!file) return { data: [] };
    return provideSemanticTokens(file, this.symbols);
  }

  foldingRanges(uri: string): FoldingRange[] {
    const file = this.workspace.getFile(uri);
    if (!file) return [];
    return provideFoldingRanges(file);
  }

  rename(
    uri: string,
    line: number,
    character: number,
    newName: string
  ): WorkspaceEdit | null {
    return provideRename(
      this.workspace,
      this.symbols,
      this.cursor,
      this.refs,
      uri,
      line,
      character,
      newName
    );
  }

  prepareRename(uri: string, line: number, character: number) {
    return prepareRename(this.workspace, this.symbols, this.cursor, uri, line, character);
  }

  formatting(uri: string, options?: FormatterOptions): TextEdit[] {
    const file = this.workspace.getFile(uri);
    if (!file) return [];
    const resolved: FormatterOptions =
      options ?? { indentSize: this.workspace.config.formatter.indent };
    return formatDocument(file, resolved);
  }

  codeActions(
    uri: string,
    range: Range,
    diagnostics: readonly import("vscode-languageserver").Diagnostic[]
  ): CodeAction[] {
    const file = this.workspace.getFile(uri);
    if (!file) return [];
    return provideCodeActions(this.workspace, uri, range, diagnostics);
  }

  codeLens(uri: string): CodeLens[] {
    const file = this.workspace.getFile(uri);
    if (!file) return [];
    return provideCodeLens(file, this.symbols, this.refs);
  }


  documentLinks(uri: string): DocumentLink[] {
    const file = this.workspace.getFile(uri);
    if (!file) return [];
    return provideDocumentLinks(file);
  }

  selectionRanges(uri: string, positions: readonly Position[]): SelectionRange[] {
    const file = this.workspace.getFile(uri);
    if (!file) return [];
    return provideSelectionRanges(this.workspace, uri, positions);
  }

  signatureHelp(
    uri: string,
    line: number,
    character: number
  ): SignatureHelp | null {
    return provideSignatureHelp(this.workspace, this.symbols, uri, line, character);
  }

  prepareCallHierarchy(
    uri: string,
    line: number,
    character: number
  ): CallHierarchyItem[] {
    return prepareCallHierarchy(
      this.workspace,
      this.symbols,
      this.cursor,
      uri,
      line,
      character
    );
  }

  incomingCalls(item: CallHierarchyItem): CallHierarchyIncomingCall[] {
    return incomingCalls(this.workspace, this.symbols, item);
  }

  outgoingCalls(item: CallHierarchyItem): CallHierarchyOutgoingCall[] {
    return outgoingCalls(this.workspace, this.symbols, item);
  }

  private rebuildIndexes(): void {
    this.symbols = new SymbolTable(this.workspace);
    this.refs = new ReferenceIndex(this.workspace, this.symbols);
    this.cursor = new CursorContext(this.workspace);
  }
}
